import { computed } from 'vue';
import { formatFilterTitle } from './formatFilterTitle';
import ru from '../i18n/ru.json';
import { useScreenSize } from '@experience/shared';
import { useCategoryStore } from '@/shared/store';
import { useI18n } from '@experience/i18n';
import { EExperienceFilterType } from '@/widgets/filtersBlock';
import type { ToursActivity } from '@/entities/tour-activities';

export function useTourActivitiesTitle() {
  const { t } = useI18n({
    inheritLocale: true,
    messages: {
      ru,
    },
  });

  const categoryStore = useCategoryStore();

  const { isMobile } = useScreenSize();

  const slugToName = computed<Record<string, string>>(() => {
    const map: Record<string, string> = {};
    (categoryStore.toursActivities ?? []).forEach((a: ToursActivity) => {
      if (a.slug) {
        map[a.slug] = a.name ?? a.slug;
      }
    });

    return map;
  });

  const selectedAfterSearchSlugs = computed<string[]>(
    () => categoryStore.filters[EExperienceFilterType.TOUR_ACTIVITY_SLUGS].tour_activity_slugs ?? [],
  );
  const selectedBeforeSearchSlugs = computed<string[]>(
    () => categoryStore.tmpFilters[EExperienceFilterType.TOUR_ACTIVITY_SLUGS].tour_activity_slugs ?? [],
  );

  const selectedLabels = computed<string[]>(() => {
    if (selectedAfterSearchSlugs.value.length === 0) {
      return [];
    }

    return selectedAfterSearchSlugs.value?.map((s) => slugToName.value[s] ?? s);
  });

  const isActive = computed(() => selectedAfterSearchSlugs.value.length > 0);
  const isDisabledResetButton = computed(() => selectedBeforeSearchSlugs.value.length === 0);

  const defaultTitle = t('tour-activities.title');

  const capitalize = (str: string) => {
    return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
  };

  const normalizedLabels = computed(() => {
    return selectedLabels.value.map((label, index) => (index === 0 ? capitalize(label) : label.toLowerCase()));
  });

  const title = computed(() => {
    if (!normalizedLabels.value.length) {
      return defaultTitle;
    }

    return formatFilterTitle(normalizedLabels.value.join(', '), selectedAfterSearchSlugs.value.length, isMobile.value);
  });

  return { title, isActive, isDisabledResetButton };
}
